import { useTranslation } from 'react-i18next';

import classNames from 'classnames';

import { StyledBadge } from 'components/shared';
import { usePalletsTypes } from 'constants/palletTypes';
import { useBallanceTypeLabels } from 'constants/transferActionOverview/balanceTypeLabels';
import { BalanceType, PalletType, TransferActionType } from 'interfaces';

import styles from './PalletsTransferHistoryActionBodyItem.module.css';

export type PalletsTransferHistoryActionBodyItemProps = {
   actionType: TransferActionType;
   address: string;
   amount: number;
   palletType: PalletType;
   balanceAfter: number;
   balanceType: BalanceType;
   user: string;
   effectiveDate: string;
};

const AMOUNT_SYMBOLS_NUMBER = 5;
const AMOUNT_DEFAULT_SYMBOL = '0';
const MINUS__SIGN = '-';

const PalletsTransferHistoryActionBodyItem = ({
   actionType,
   address,
   amount,
   palletType,
   balanceAfter,
   balanceType,
   user,
   effectiveDate,
}: PalletsTransferHistoryActionBodyItemProps) => {
   const { t } = useTranslation();

   const { PALLET_TYPE_LABELS } = usePalletsTypes();
   const { BALANCE_TYPE_LABELS } = useBallanceTypeLabels();

   const isAmountPositive = amount > 0;
   const isBalanceAfterPositive = balanceAfter > 0;
   const formattedAmount = Math.abs(amount)
      .toString()
      .padStart(AMOUNT_SYMBOLS_NUMBER, AMOUNT_DEFAULT_SYMBOL);
   const amountSign = isAmountPositive ? '' : MINUS__SIGN;
   const fullAmount = amountSign + formattedAmount;
   const balanceTypeLabel = BALANCE_TYPE_LABELS[balanceType];

   let actionVerbLabel;

   if (actionType === 'EDIT_BALANCE') {
      actionVerbLabel = isAmountPositive
         ? t('palletsTransferHistory.editBalance.addedTo')
         : t('palletsTransferHistory.editBalance.removedFrom');
   } else {
      actionVerbLabel = isAmountPositive
         ? t('palletsTransferHistory.transfer.acceptedTo')
         : t('palletsTransferHistory.transfer.sentFrom');
   }

   return (
      <li className={styles.listItem}>
         <div className={styles.leftPartContainer}>
            <span className={styles.greyText}>{effectiveDate}</span>
            <StyledBadge
               variant={isAmountPositive ? 'POSITIVE' : 'NEGATIVE'}
               text={fullAmount}
               className={styles.amount}
            />
            <span className={classNames(styles.palletsType, styles.boldText)}>
               {PALLET_TYPE_LABELS[palletType]}
            </span>
            <span className={styles.actionVerb}>{actionVerbLabel}</span>
            <div className={styles.multipleRowsContainer}>
               <span className={styles.boldText}>{user}</span>
               {address && <span className={styles.greyText}>{address}</span>}
               <span className={styles.greyText}>
                  {t('palletsTransferHistory.balanceType')}: {balanceTypeLabel}
               </span>
            </div>
         </div>
         <div className={classNames(styles.balanceAfter, styles.greyText)}>
            {t('palletsTransferHistory.balanceAfter')}:
            <span
               className={
                  isBalanceAfterPositive ? styles.positiveBalanceAfter : styles.negativeBalanceAfter
               }
            >
               {balanceAfter}
            </span>
         </div>
      </li>
   );
};

export default PalletsTransferHistoryActionBodyItem;
